/*
Species info loader — mirrors Android SpeciesInfo.kt.
Loads /data/species-info.json (an array of entries keyed by speciesIndex `i`)
and exposes a Map<int, SpeciesEntry> for screens that need Paris-wide stats,
Wikipedia summary, and the city's PDF fiche per species.
*/

import { fetchJson } from './fetchJson'

const kAssetPath = '/data/species-info.json'  // canonical location served by the SW

export interface SpeciesStats
{
	count: number
	proportion: number
	medianHm: number | null
	medianCircCm: number | null
	topArrAbs: Array<{ arr: string; count: number }>
	topArrOver: Array<{ arr: string; ratio: number; count: number }>
}

export interface SpeciesEntry
{
	i: number
	stats: SpeciesStats
	wp?: string
	qid?: string
	summary?: string
	pdf?: string
}

let fEntries: Map<number, SpeciesEntry> | null = null  // memoized index -> entry, null until first load

/*
Ensure species-info is loaded and indexed by `i`. Idempotent. Throws on
fetch failure; consumers should fall back to a graceful empty state.
*/
export async function ensureSpeciesInfo(): Promise<Map<number, SpeciesEntry>>
{
	if (fEntries) return fEntries
	const vArr = await fetchJson<SpeciesEntry[]>(kAssetPath)  // throws with context on bad JSON
	const vMap = new Map<number, SpeciesEntry>()  // built then handed to module singleton
	for (const vEntry of vArr) vMap.set(vEntry.i, vEntry)
	fEntries = vMap
	return fEntries
}

/*
Look up the entry for a given speciesIndex. Returns null if not loaded yet
or unknown — caller treats this as a soft miss (skip the optional fields).
*/
export function speciesInfoFor(inIndex: number): SpeciesEntry | null
{
	if (!fEntries) return null
	return fEntries.get(inIndex) ?? null
}
